import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import MonacoEditor from "react-monaco-editor";
import * as monaco from "monaco-editor";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button, Card, CardBody, CardHeader, CardTitle } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faCheck, faRobot, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { requestPromptLabClaude } from "../../services/RobotAIServices.js";
import { extractFirstCodeBlock } from "../../utils/extractCode.js";
import ListenButton from "../LessonAudio/ListenButton";
import PaneResizer, { clampSize } from "./PaneResizer.js";
import { LiveStatus, useStatus, useOnChange, refocusIfLost } from "./workspaceA11y.js";
import "./PromptLabLayout.css";
import "./EditorLayout.css";
import { AiFirstUseNotice, AiMistakesNote } from "../AiNotice/AiNotice";

/**
 * Module 3 ("editor") layout:
 *
 *   +------------+‖----------------+‖--------------------+
 *   |  Lesson    |‖  Code Editor   |‖  Console           |
 *   |  markdown  |‖  (+ Run/Tests) |‖  + "Ask Claude"    |
 *   +------------+‖----------------+‖--------------------+
 *
 * No Prompt Lab column — the student writes and reads code directly. A slim
 * "Ask Claude about this output" hook sits at the base of the console and uses
 * the same prompt-lab Claude service, seeded with the current code + output.
 *
 * The three panels are separated by draggable split-pane dividers (`.pl-resizer`
 * — see PromptLabLayout.css): the whole border is the drag target, and the arrow
 * keys move it too (see PaneResizer).
 */
const EditorLayout = ({
    definition,
    lessonTitle,
    editorCode,
    setEditorCode,
    language,
    consoleOutput,
    onRun,
    isRunning,
    testResults,
    allPassed,
    askHelpSignal,
    solveSignal,
}) => {
    const { t } = useTranslation();
    const [leftWidth, setLeftWidth] = useState(380);
    const [middleWidth, setMiddleWidth] = useState(480);
    const containerRef = useRef(null);

    // Slim "Ask Claude" helper (independent of grading).
    const [askOpen, setAskOpen] = useState(false);
    const [askInput, setAskInput] = useState("");
    const [askMessages, setAskMessages] = useState([]);
    const [asking, setAsking] = useState(false);
    const askEndRef = useRef(null);
    const askInputRef = useRef(null);
    const sendHintId = useId();
    // Set when "do it for me" puts Claude's program in the editor, so the run waits
    // for that code to land instead of grading what was there before.
    const runAfterSolveRef = useRef(false);

    // Initial split sizes — lesson gets ~28%, editor/console split the rest.
    useEffect(() => {
        const setInitial = () => {
            const total = containerRef.current ? containerRef.current.clientWidth : window.innerWidth;
            const left = Math.min(460, Math.max(280, Math.floor(total * 0.28)));
            const rest = total - left;
            setLeftWidth(left);
            setMiddleWidth(Math.floor(rest / 2));
        };
        setInitial();
        window.addEventListener("resize", setInitial);
        return () => window.removeEventListener("resize", setInitial);
    }, []);

    useEffect(() => {
        try {
            monaco.editor.defineTheme("promptLabTheme", {
                base: "vs-dark",
                inherit: true,
                rules: [],
                colors: { "editor.background": "#272727" },
            });
        } catch (e) {
            // monaco may not be ready in some test envs; ignore
        }
    }, []);

    useEffect(() => {
        if (askEndRef.current) askEndRef.current.scrollIntoView({ behavior: "smooth" });
    }, [askMessages.length, asking]);

    // Each divider's limits for a given width, shared by dragging and the keyboard.
    const leftLimits = (width) => ({ min: 260, max: width - middleWidth - 12 - 220 });
    const middleLimits = (width) => ({ min: 280, max: width - leftWidth - 12 - 220 });
    const containerWidth = () => containerRef.current?.clientWidth || window.innerWidth;

    // Drag the lesson ↔ editor border → resize the lesson column.
    const startLeftResize = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = leftLimits(rect.width);
            setLeftWidth(clampSize(ev.clientX - rect.left, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    // Drag the editor ↔ console border → resize the editor column (right fills).
    const startMiddleResize = (e) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev) => {
            const { min, max } = middleLimits(rect.width);
            setMiddleWidth(clampSize(ev.clientX - rect.left - leftWidth - 6, min, max));
        };
        const stop = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", stop);
            document.body.classList.remove("is-resizing-col");
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", stop);
        document.body.classList.add("is-resizing-col");
    };

    const editorLabel = t("promptLab.codeEditor", { lang: language || "code" });

    const editorOptions = {
        selectOnLineNumbers: true,
        readOnly: false,
        fontSize: 14,
        wordWrap: "on",
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        ariaLabel: editorLabel,
        accessibilitySupport: "auto",
    };

    // `overrideQuestion` comes from the navbar's "Stuck?" panel. With `autoApply` the
    // ask becomes "do it for me": Claude is told to hand back the finished program
    // (the normal tutor prompt below forbids exactly that), and the code block it
    // returns goes into the editor and is run.
    const handleAsk = async (overrideQuestion, { autoApply = false } = {}) => {
        const question = (typeof overrideQuestion === "string" ? overrideQuestion : askInput).trim();
        if (!question || asking) return;

        const userMsg = { role: "user", content: question };
        // Seed Claude with the student's code + console output as context.
        const contextual = `Here is my code:\n\n\`\`\`${language || "python"}\n${editorCode || "(empty)"}\n\`\`\`\n\nConsole output:\n\`\`\`\n${consoleOutput || "(none)"}\n\`\`\`\n\nMy question: ${question}`;
        const sendMessages = [...askMessages, { role: "user", content: contextual }];

        setAskMessages((prev) => [...prev, userMsg]);
        if (typeof overrideQuestion !== "string") setAskInput("");
        setAsking(true);

        try {
            const sys = autoApply
                ? `You are finishing this exercise FOR the student, at their request. Reply with the complete, correct, final ${language || "python"} program in a single fenced code block, then one short sentence saying what it does. No partial snippets, no placeholders.`
                : definition?.systemPromptForClaude
                || "You are a friendly coding tutor. Explain the student's code and console output clearly and simply, and nudge them toward the fix — never paste a full solution.";
            const result = await requestPromptLabClaude(sys, sendMessages, definition?.lessonID);
            const text = result?.text || t("promptLab.noResponse");
            setAskMessages((prev) => [...prev, { role: "assistant", content: text }]);

            if (autoApply) {
                const block = extractFirstCodeBlock(text, language);
                if (block?.code) {
                    setEditorCode(block.code);
                    // Run once the parent's editorCode has committed — see the effect below.
                    runAfterSolveRef.current = true;
                }
            }
        } catch (e) {
            setAskMessages((prev) => [...prev, { role: "assistant", content: t("promptLab.askCouldNotReach") }]);
        } finally {
            setAsking(false);
        }
    };

    const handleAskKeyDown = (e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleAsk();
        }
    };

    // Send keeps focus in the box; the button disables itself once the box is
    // empty, which would otherwise drop focus to <body>.
    const askFromButton = () => {
        handleAsk();
        askInputRef.current?.focus();
    };

    // Spoken summaries: a reply is coming or has come, and how a run went.
    const [status, announce] = useStatus();

    useOnChange(asking, (busy) => {
        if (busy) return announce(t("promptLab.statusReplying"));
        announce(t("promptLab.statusReplied"));
        refocusIfLost(askInputRef.current);
    });

    useOnChange(!!isRunning, (running) => {
        if (running) return announce(t("promptLab.running"));
        const total = Array.isArray(testResults) ? testResults.length : 0;
        announce(
            total > 0
                ? t("promptLab.statusRunTests", { passed: testResults.filter((r) => r.passed).length, total })
                : t("promptLab.statusRunFinished")
        );
    });

    // Driven by the navbar's "Stuck?" panel. The helper is collapsed by default, so
    // both open it first — otherwise the answer lands somewhere the student can't see.
    useEffect(() => {
        if (!askHelpSignal) return;
        setAskOpen(true);
        handleAsk(t("promptLab.hintAskMessage"));
    }, [askHelpSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!solveSignal) return;
        setAskOpen(true);
        handleAsk(t("promptLab.hintSolveCodeMessage"), { autoApply: true });
    }, [solveSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    // The solved code has committed upstream, so running now grades it and not the
    // code it replaced.
    useEffect(() => {
        if (!runAfterSolveRef.current) return;
        runAfterSolveRef.current = false;
        onRun?.();
    }, [editorCode]); // eslint-disable-line react-hooks/exhaustive-deps

    const dragTitle = t("promptLab.dragToResize", "Drag to resize");

    // Exercise "Passed" badge, shown once all tests pass. `allPassed` also covers
    // a lesson restored as already complete (before any test run this session).
    const hasTestRun = Array.isArray(testResults) && testResults.length > 0;
    const exercisePassed = allPassed || (hasTestRun && testResults.every((r) => r.passed));

    return (
        <div ref={containerRef} className="prompt-lab-root">
            {/* LEFT: Lesson */}
            <div
                className="prompt-lab-pane prompt-lab-pane--lesson"
                style={{ width: leftWidth, flex: "0 0 auto" }}
            >
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <CardTitle tag="h5" className="mb-0">{lessonTitle || definition?.title || t("promptLab.lessonFallback")}</CardTitle>
                            <ListenButton text={definition?.lessonMarkdown} lessonID={definition?.lessonID} />
                        </CardHeader>
                        <CardBody className="prompt-lab-lesson-body">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {definition?.lessonMarkdown || ""}
                            </ReactMarkdown>

                            {definition?.exercisePrompt && (
                                <div className={`prompt-lab-exercise${exercisePassed ? " prompt-lab-exercise--passed" : ""}`}>
                                    <div className="prompt-lab-exercise-head">
                                        <strong>{t("promptLab.yourExercise")}</strong>
                                        {exercisePassed && (
                                            <span className="prompt-lab-exercise-badge passed">
                                                <FontAwesomeIcon icon={faCheck} />
                                                {t("promptLab.passed")}
                                            </span>
                                        )}
                                    </div>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {definition.exercisePrompt}
                                    </ReactMarkdown>
                                </div>
                            )}

                        </CardBody>
                    </Card>
                </div>
            </div>

            {/* Draggable border: Lesson ↔ Editor */}
            <PaneResizer
                value={leftWidth}
                {...leftLimits(containerWidth())}
                onResize={setLeftWidth}
                onPointerDown={startLeftResize}
                label={t("promptLab.resizePane", { pane: t("promptLab.lessonFallback") })}
                title={dragTitle}
            />

            {/* MIDDLE: Code Editor */}
            <div
                className="prompt-lab-pane prompt-lab-pane--chat"
                style={{ width: middleWidth, flex: "0 0 auto" }}
            >
                <div className="prompt-lab-pane-inner">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header prompt-lab-editor-header">
                            <span>{editorLabel}</span>
                            <span className="editor-layout-actions">
                                <Button onClick={onRun} size="sm" color="success" disabled={!editorCode}>
                                    <FontAwesomeIcon icon={faPlay} /> &nbsp; {t("promptLab.run")}
                                </Button>
                            </span>
                        </CardHeader>
                        <div className="pl-editor-hint">{t("promptLab.editorKeyboardHint")}</div>
                        <CardBody className="prompt-lab-editor-body">
                            <MonacoEditor
                                language={language || "python"}
                                theme="vs-dark"
                                value={editorCode || ""}
                                onChange={(value) => setEditorCode(value)}
                                options={editorOptions}
                            />
                        </CardBody>
                    </Card>
                </div>
            </div>

            {/* Draggable border: Editor ↔ Console */}
            <PaneResizer
                value={middleWidth}
                {...middleLimits(containerWidth())}
                onResize={setMiddleWidth}
                onPointerDown={startMiddleResize}
                label={t("promptLab.resizePane", { pane: editorLabel })}
                title={dragTitle}
            />

            {/* RIGHT: Console + slim Ask Claude */}
            <div className="prompt-lab-pane prompt-lab-pane--right" style={{ flex: "1 1 auto" }}>
                <div className="editor-layout-console-wrap">
                    <Card className="prompt-lab-card">
                        <CardHeader className="prompt-lab-card-header">
                            <span>{t("promptLab.console")}</span>
                        </CardHeader>
                        <CardBody className="prompt-lab-console">
                            <pre className="prompt-lab-console-output">
                                {consoleOutput || t("promptLab.noOutputYet")}
                            </pre>
                        </CardBody>
                    </Card>
                </div>

                {/* Slim "Ask Claude about this output" hook */}
                <div className="editor-ask-claude">
                    {askOpen && (
                        <div className="editor-ask-claude-thread">
                            {/* A log, so new messages are read out; the status line
                                covers "Thinking…". */}
                            <div className="editor-ask-claude-log" role="log" aria-live="polite" aria-relevant="additions">
                                {askMessages.map((m, i) => (
                                    <div key={i} className={`prompt-lab-msg prompt-lab-msg--${m.role}`}>
                                        <div className="prompt-lab-msg-role">{m.role === "user" ? t("promptLab.you") : "Claude"}</div>
                                        <div className="prompt-lab-msg-content">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ""}</ReactMarkdown>
                                        </div>
                                    </div>
                                ))}
                                {asking && (
                                    <div className="prompt-lab-msg prompt-lab-msg--assistant prompt-lab-msg--thinking" aria-hidden="true">
                                        <div className="prompt-lab-msg-content">{t("promptLab.thinking")}</div>
                                    </div>
                                )}
                                <div ref={askEndRef} />
                            </div>
                            <AiFirstUseNotice />
                            <div className="editor-ask-claude-input">
                                {/* Read-only, not disabled, while Claude replies, so
                                    keyboard focus stays here. */}
                                <textarea
                                    ref={askInputRef}
                                    value={askInput}
                                    onChange={(e) => setAskInput(e.target.value)}
                                    onKeyDown={handleAskKeyDown}
                                    placeholder={t("promptLab.askOutputPlaceholder")}
                                    rows={2}
                                    aria-label={t("promptLab.messageLabel")}
                                    aria-describedby={sendHintId}
                                    readOnly={asking}
                                    aria-busy={asking}
                                />
                                <Button
                                    size="sm"
                                    color="primary"
                                    onClick={askFromButton}
                                    disabled={asking || !askInput.trim()}
                                    aria-label={t("promptLab.send")}
                                    title={t("promptLab.send")}
                                >
                                    <FontAwesomeIcon icon={faPaperPlane} />
                                </Button>
                            </div>
                            <div id={sendHintId} className="pl-chat-hint">{t("promptLab.sendShortcutHint")}</div>
                            <AiMistakesNote />
                        </div>
                    )}
                    <button
                        type="button"
                        className="editor-ask-claude-toggle"
                        onClick={() => setAskOpen((o) => !o)}
                        aria-expanded={askOpen}
                    >
                        <FontAwesomeIcon icon={faRobot} /> &nbsp;
                        {askOpen ? t("promptLab.hideClaude") : t("promptLab.askClaudeStuck")}
                    </button>
                </div>
            </div>

            <LiveStatus message={status} />
        </div>
    );
};

export default EditorLayout;
